import { cx } from '@emotion/css';
import { Box, Stack, Typography } from '@mui/material';
import { GridEventListener, GridProSlotsComponent } from '@mui/x-data-grid-pro';
import { includes, partition, uniqueId } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';

import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Draghandle } from '@/components/lists/DragAndDropList/Draghandle';
import { Footer, FOOTER_HEIGHT } from '@/components/navigation/Footer';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { PageSizes } from '@/components/tables/DataTable/constants';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import {
  PaginatedTableProps,
  usePaginatedDataTableQuery,
} from '@/components/tables/DataTable/hooks/usePaginatedDataTableQuery';
import { useReportError } from '@/hooks/useReportError';
import { COLORS } from '@/styles/tokens/colors';
import { BalanceSheetCategoryType } from '@/types/schema';

import { useBalanceSheetCategoriesColumns } from './AdminBalanceSheetConfigurationPage.columns';
import {
  BalanceSheetCategoriesForm,
  NEW_BALANCE_SHEET_CATEGORY_SENTINEL,
  RowData,
} from './AdminBalanceSheetConfigurationPage.types';
import {
  getDefaultValues,
  getNextRowOrder,
  mapDataToRows,
  mapFormValuesToInputs,
} from './AdminBalanceSheetConfigurationPage.utils';
import {
  ReorderableRowsProvider,
  useReorderableRows,
} from './context/ReorderableRows.context';
import {
  useBalanceSheetCategoriesQuery,
  useUpdateBalanceSheetCategoriesMutation,
} from './graphql/AdminBalanceSheetConfigurationPage.generated';

const useStyles = makeStyles()((_theme) => ({
  virtualScroller: {
    minHeight: 170,
  },
}));

// Styles for pinned rows - using sx prop because MUI DataGrid Pro doesn't pass
// the classes.pinnedRows prop to the GridPinnedRows component
const pinnedRowsSx = {
  '& .MuiDataGrid-pinnedRows': {
    borderTop: `1px solid ${COLORS.GRAY[300]}`,
    boxShadow: 'none',
    position: 'relative',
    zIndex: 0,
  },
  // Ensure pinned rows maintain their background on hover (prevent transparent hover effect)
  '& .MuiDataGrid-pinnedRows .MuiDataGrid-row': {
    backgroundColor: COLORS.GRAY[50],
  },
  '& .MuiDataGrid-pinnedRows .MuiDataGrid-row:hover': {
    backgroundColor: COLORS.GRAY[50],
  },
};

const slots: Partial<GridProSlotsComponent> = {
  rowReorderIcon: () => (
    <CellContainer>
      <Draghandle />
    </CellContainer>
  ),
};

interface AdminBalanceSheetConfigurationPageInnerProps {
  pinnedRows: RowData[];
  paginatedTableProps: PaginatedTableProps;
  pageToggle: React.ReactNode;
}

function AdminBalanceSheetConfigurationPageInner({
  paginatedTableProps,
  pageToggle,
  pinnedRows,
}: AdminBalanceSheetConfigurationPageInnerProps) {
  const { reorderableRows, setReorderableRows } = useReorderableRows();
  const { setValue } = useFormContext<BalanceSheetCategoriesForm>();
  const columns = useBalanceSheetCategoriesColumns();
  const styles = useStyles();

  const addNewBalanceSheetCategory = useCallback(() => {
    const id = uniqueId(NEW_BALANCE_SHEET_CATEGORY_SENTINEL);

    setValue(`balanceSheetCategoriesById.${id}`, {
      id,
      name: '',
      assetClassIds: [],
      _isSystemClass: false,
    });

    setReorderableRows((prev) => [
      {
        __reorder__: 'New category',
        id,
        name: '',
        isSystemClass: false,
        assetClasses: null,
        action: null,
        type: BalanceSheetCategoryType.Asset,
      },
      ...prev,
    ]);

    setValue(`currentlyEditingRowId`, id);
  }, [setReorderableRows, setValue]);

  const handleRowOrderChange: GridEventListener<'rowOrderChange'> = useCallback(
    (params) => {
      const nextRowOrder = getNextRowOrder(reorderableRows, params);
      setReorderableRows(nextRowOrder);
    },
    [reorderableRows, setReorderableRows]
  );

  return (
    <DataTableCard>
      <Stack spacing={2}>
        <Stack
          spacing={2}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {pageToggle}
          <Button
            variant="secondary"
            size="sm"
            startIcon={PlusIcon}
            onClick={addNewBalanceSheetCategory}
          >
            Add balance sheet category
          </Button>
        </Stack>

        <Typography variant="body-s">
          Manage balance sheet categories and reorder as desired. Associating
          asset categories determines which assets will display under each
          balance sheet category.
        </Typography>

        <DataTable
          {...paginatedTableProps}
          hideFooterPagination
          classes={{
            virtualScroller: cx(styles.classes.virtualScroller),
          }}
          sx={pinnedRowsSx}
          disableColumnReorder
          disableColumnResize
          pagination={false}
          slots={slots}
          columns={columns}
          rows={reorderableRows}
          pinnedRows={{
            bottom: pinnedRows,
          }}
          onRowOrderChange={handleRowOrderChange}
          rowReordering
          rowHoverVariant="transparent"
        />
      </Stack>
    </DataTableCard>
  );
}

interface AdminBalanceSheetConfigurationPageProps {
  pageToggle: React.ReactNode;
}

export function AdminBalanceSheetConfigurationPage(
  props: AdminBalanceSheetConfigurationPageProps
) {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const [reorderableRows, setReorderableRows] = useState<RowData[]>([]);

  const [paginatedTableProps, { data, refetch }] = usePaginatedDataTableQuery(
    useBalanceSheetCategoriesQuery,
    {
      pageSize: PageSizes.FiveHundred,
      variables: {
        where: {},
      },
      // without this, the query will use cached data if it exists even right after
      // the mutation
      fetchPolicy: 'network-only',
    }
  );

  const [updateBalanceSheetCategories] =
    useUpdateBalanceSheetCategoriesMutation({
      onCompleted: () => {
        showFeedback('Successfully updated balance sheet categories', {
          variant: 'success',
        });
        void refetch();
      },
      onError: (err) => {
        reportError('failed to update balance sheet categories', err);
        showFeedback(
          'Failed to update balance sheet categories. Please refresh the page and try again.'
        );
      },
    });

  const rows = useMemo(() => mapDataToRows(data ?? []), [data]);

  // initialize reorderable rows from the query into the local state
  const { reorderableRows: reorderableRowsFromQuery, pinnedRows } =
    useMemo(() => {
      const specialCategoryTypes = [
        BalanceSheetCategoryType.Liabilities,
        BalanceSheetCategoryType.Insurance,
      ];

      const [pinnedRows, reorderableRows] = partition(rows, (row) =>
        includes(specialCategoryTypes, row.type)
      );
      return { pinnedRows, reorderableRows };
    }, [rows]);

  useEffect(() => {
    if (reorderableRowsFromQuery.length > 0) {
      setReorderableRows(reorderableRowsFromQuery);
    }
  }, [reorderableRowsFromQuery]);

  const formMethods = useForm<BalanceSheetCategoriesForm>({
    defaultValues: {
      balanceSheetCategoriesById: {},
    },
  });

  const {
    formState: { isSubmitting },
  } = formMethods;

  const { reset } = formMethods;
  useEffect(() => {
    if (!data) return;
    const defaultValues = getDefaultValues(data ?? []);
    reset(defaultValues);
  }, [data, reset]);

  const handleSubmit = formMethods.handleSubmit((formValues) => {
    const { creates, updates, clears } = mapFormValuesToInputs(formValues, {
      reorderableRows,
    });
    return updateBalanceSheetCategories({
      variables: {
        createInputs: creates,
        updateInputs: updates,
        clearAssetClassInputs: clears,
      },
    });
  });

  return (
    <ReorderableRowsProvider
      reorderableRows={reorderableRows}
      setReorderableRows={setReorderableRows}
    >
      <Box>
        <FormProvider {...formMethods}>
          <Box mb={`${FOOTER_HEIGHT + 10}px`}>
            <AdminBalanceSheetConfigurationPageInner
              {...props}
              paginatedTableProps={paginatedTableProps}
              pinnedRows={pinnedRows}
            />
          </Box>
        </FormProvider>
        <Box position="fixed" sx={{ bottom: 0, left: 0, right: 0 }}>
          <Footer
            rightAction={
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                Save changes
              </Button>
            }
          />
        </Box>
      </Box>
    </ReorderableRowsProvider>
  );
}
